import Provider from "./new/provider";
import ObjectModel from "../models/objectModel";
import {IProviderRoutes} from "../interfaces/provider";
import {Constructor} from "../../global/constructor";
import {
  IObjectModelEntryPerformActionResponse,
  IObjectModelPropertiesResponse,
  IObjectModelsSearchResponse,
  IResponse,
  IServerData
} from "../interfaces/serverInterfaces";
import {HTTPMethod} from "./new/httpMethod";
import {Route} from "../enums/route";
import {RouteHelper} from "../helpers/routeHelper";
import {Hash} from "~/interfaces/hashInterfaces";
import action from "../../formability/detailViewBuilder/models/action";

interface IObjectModelProviderRoutes {
  fetch: string;
  properties: string;
  publish: Route;
  publishingDependencies: Route;
  fetchPublishedObjectModel: Route;
  relatedListViews: Route;
  show: Route;
  fetchLinkedForms: Route;
  relations: Route;
  performAction: Route;
}

export default class ObjectModelProvider<T extends ObjectModel = ObjectModel> extends Provider<T, any, any> {
  routes: IProviderRoutes & IObjectModelProviderRoutes;

  constructor(protected modelType: Constructor<T>, public modelRoot: string, public collectionRoot: string) {
    super(modelType, modelRoot, collectionRoot);
    let newRoutes: IObjectModelProviderRoutes = {
      fetch: RouteHelper.path({route: Route.objectModels}),
      properties: RouteHelper.path({route: Route.objectModelProperties}),
      publish: Route.publishObjectModel,
      publishingDependencies: Route.objectModelPublishingDependencies,
      fetchPublishedObjectModel: Route.objectModelPublishedObjectModel,
      relatedListViews: Route.objectModelRelatedListViews,
      show: Route.objectModelShow,
      fetchLinkedForms: Route.objectModelLinkedForms,
      relations: Route.objectModelRelations,
      performAction: Route.objectModelPerformAction,
    };
    Object.assign(this.routes, newRoutes);
  }

  fetch(ids: object, context: string = null): Promise<IServerData<IObjectModelsSearchResponse>> {
    return new Promise<IServerData<IObjectModelsSearchResponse>>((resolve, reject) => {
      this.serverAction<IObjectModelsSearchResponse>({
        endpoint: this.routes.fetch,
        method: HTTPMethod.GET,
        json: {ids, context},
      }).then(resolve);
    });
  }

  fetchPropertiesFromClientId(clientId: string): Promise<IServerData<IObjectModelPropertiesResponse>> {
    return new Promise<IServerData<IObjectModelPropertiesResponse>>((resolve, reject) => {
      this.serverAction<IObjectModelPropertiesResponse>({
        endpoint: this.routes.properties.replace(':id', clientId),
        method: HTTPMethod.GET,
      }).then(resolve);
    });
  }

  publish(obj: T): Promise<IServerData> {
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction<IResponse>({
        endpoint: RouteHelper.path({route: this.routes.publish, modelOrId: obj}),
        method: HTTPMethod.POST,
      }).then(resolve)
    })
  }

  publishingDependencies(obj: T): Promise<IServerData> {
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction<IResponse>({
        endpoint: RouteHelper.path({route: this.routes.publishingDependencies, modelOrId: obj}),
        method: HTTPMethod.GET,
      }).then(resolve)
    })
  }

  fetchPublished(obj: T): Promise<IServerData> {
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction<IResponse>({
        endpoint: RouteHelper.path({route: this.routes.fetchPublishedObjectModel, modelOrId: obj}),
        method: HTTPMethod.GET,
      }).then(resolve)
    })
  }

  fetchRelatedListViews(obj: T): Promise<IServerData<IObjectModelPropertiesResponse>> {
    return new Promise<IServerData<IObjectModelPropertiesResponse>>((resolve, reject) => {
      this.serverAction<IObjectModelPropertiesResponse>({
        endpoint: RouteHelper.path({route: this.routes.relatedListViews, modelOrId: obj}),
        method: HTTPMethod.GET,
      }).then(resolve);
    });
  }

  show(obj: T): Promise<IServerData<IResponse & {object_model}>> {
    return new Promise<IServerData<IResponse & {object_model}>>((resolve, reject) => {
      this.serverAction<IResponse & {object_model}>({
        endpoint: RouteHelper.path({route: this.routes.show, modelOrId: obj}),
        method: HTTPMethod.GET,
      }).then(resolve)
    })
  }

  fetchLinkedForms(obj: T): Promise<IServerData> {
    return new Promise<IServerData>((resolve, reject) => {
      this.serverAction<IResponse>({
        endpoint: RouteHelper.path({route: this.routes.fetchLinkedForms, modelOrId: obj}),
        method: HTTPMethod.GET,
      }).then(resolve)
    });
  }

  async fetchRelations(obj: T): Promise<IServerData<Hash>> {
    return this.serverAction<Hash>({
      endpoint: RouteHelper.path({route: this.routes.relations, modelOrId: obj}),
      method: HTTPMethod.GET,
    })
  }

  performAction({object_model_id, action_id, data}: {object_model_id: string, action_id: string, data?: object}): Promise<IServerData<IObjectModelEntryPerformActionResponse>> {
    const json = data ?? {};
    json['action_id'] = action_id;
    return this.serverAction({
      endpoint: RouteHelper.path({route: this.routes.performAction, modelOrId: object_model_id}),
      method: HTTPMethod.POST,
      json
    });
  }
}